import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageSquareText, Loader2, Check, X, Trash2, Star, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [['pending', 'Pending'], ['approved', 'Approved'], ['rejected', 'Rejected']];

const Stars = ({ value }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-4 h-4 ${n <= Math.round(value) ? 'fill-amber-400 text-amber-500' : 'text-gray-300'}`} />)}
  </div>
);

const ProductReviews = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState('pending');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/product-reviews`, { params: { status } });
      setItems(data.items || []); setCounts(data.counts || {});
    } catch { setItems([]); } finally { setLoading(false); }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    if (action === 'delete' && !window.confirm('Delete this review permanently?')) return;
    try {
      if (action === 'delete') await axios.delete(`${API}/admin/product-reviews/${id}`);
      else await axios.post(`${API}/admin/product-reviews/${id}/${action}`);
      toast.success(action === 'approve' ? 'Review approved & live' : action === 'reject' ? 'Review rejected' : 'Review deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="product-reviews-admin">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareText className="w-6 h-6 text-[#00A79D]" />
        <h1 className="font-heading text-2xl font-bold text-gray-900">Product Reviews</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">Approve buyer reviews before they appear on the public product pages.</p>

      <div className="flex items-center gap-2 mb-5">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setStatus(key)} data-testid={`reviews-tab-${key}`}
            className={`text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${status === key ? 'bg-[#00A79D] text-white border-[#00A79D]' : 'text-gray-600 border-gray-200 hover:border-[#00A79D]'}`}>
            {label}{counts[key] != null ? ` (${counts[key]})` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-gray-400 py-16 text-center" data-testid="reviews-empty">No {status} reviews.</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars value={r.rating} />
                    <a href={`/products/${r.product_slug}`} target="_blank" rel="noreferrer" className="text-sm font-semibold text-gray-900 hover:text-[#00A79D] inline-flex items-center gap-1 truncate">{r.product_name || r.product_slug} <ExternalLink className="w-3 h-3" /></a>
                    <span className="text-xs text-gray-400">· {r.supplier_name}</span>
                  </div>
                  <p className="text-gray-700 text-sm mt-2 whitespace-pre-wrap">{r.comment}</p>
                  <p className="text-xs text-gray-500 mt-2">{r.reviewer_name}{r.organisation ? `, ${r.organisation}` : ''} · <span className="text-gray-400">{r.reviewer_email}</span> · {(r.created_at || '').slice(0, 10)}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {status !== 'approved' && <button onClick={() => act(r.id, 'approve')} data-testid={`review-approve-${r.id}`} title="Approve" className="p-2 rounded-md text-emerald-600 hover:bg-emerald-50"><Check className="w-4 h-4" /></button>}
                  {status !== 'rejected' && <button onClick={() => act(r.id, 'reject')} data-testid={`review-reject-${r.id}`} title="Reject" className="p-2 rounded-md text-amber-600 hover:bg-amber-50"><X className="w-4 h-4" /></button>}
                  <button onClick={() => act(r.id, 'delete')} data-testid={`review-delete-${r.id}`} title="Delete" className="p-2 rounded-md text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
